import DateFnsUtils from '@date-io/date-fns';
import { Grid, makeStyles } from '@material-ui/core';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import {
  KeyboardDatePicker,
  KeyboardDateTimePicker,
  KeyboardTimePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import { ParsableDate } from '@material-ui/pickers/constants/prop-types';
import { enUS, es, ptBR } from 'date-fns/locale';
import i18n from 'i18n';
import React from 'react';
import styles from './styles';

const useStyles = makeStyles(styles);

const FORMAT_DEFAULT = {
  date: 'dd/MM/yyyy',
  time: 'HH:mm',
  datetime: 'dd/MM/yyyy HH:mm',
};

export interface DatePickerProps {
  id: string;
  blockTypeDate?: boolean;
  type: 'date' | 'time' | 'datetime' | 'datetimeFilter';
  label: string;
  value: ParsableDate;
  format?: string;
  minDate?: ParsableDate;
  keyValue: string;
  helperText?: string;
  defaultValue?: unknown;
  disabled?: boolean;
  shouldUpdateOnAccept?: boolean;
  shrink?: boolean;
  disablePast?: boolean;
  error?: boolean;
  onError?: (error: any) => void;
  setFieldsValue: (key: string, value: ParsableDate) => void;
}

const DatePicker = ({
  id,
  blockTypeDate,
  type,
  label,
  value,
  format,
  minDate,
  keyValue,
  helperText,
  defaultValue,
  disabled,
  shouldUpdateOnAccept = true,
  shrink,
  disablePast,
  error,
  onError,
  setFieldsValue,
}: DatePickerProps) => {
  const classes = useStyles();
  const [currentDate, setCurrentDate] = React.useState<ParsableDate>(value);
  const [errors, setErrors] = React.useState();
  const [dateOpen, setDateOpen] = React.useState(false);

  const handleErrors = error => {
    setErrors(error);
    // eslint-disable-next-line no-unused-expressions
    onError?.(error);
  };

  React.useEffect(() => {
    if (!shouldUpdateOnAccept) setCurrentDate(value);
    setFieldsValue(keyValue, currentDate);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, keyValue]);

  React.useEffect(() => {
    setCurrentDate(value);
  }, [value]);

  const localeDictionary = {
    'pt-BR': ptBR,
    'en-US': enUS,
    es,
  };

  return (
    <Grid data-testid={'autosky-datepicker'}>
      <MuiPickersUtilsProvider
        locale={localeDictionary[i18n.language]}
        utils={DateFnsUtils}
      >
        {type === 'date' && (
          <KeyboardDatePicker
            fullWidth
            format={format || FORMAT_DEFAULT[type]}
            helperText={errors && helperText}
            id={id}
            value={currentDate}
            minDate={minDate}
            label={label}
            inputVariant="outlined"
            margin="normal"
            className={classes.container}
            onError={handleErrors}
            onChange={setCurrentDate}
            disabled={disabled}
            InputLabelProps={{ shrink: true }}
            error={error}
            open={blockTypeDate ? dateOpen : undefined}
            onClick={() => {
              if (blockTypeDate && !disabled) {
                setDateOpen(!dateOpen);
              }
            }}
            onClose={() => {
              if (blockTypeDate && !disabled) {
                setDateOpen(false);
              }
            }}
          />
        )}

        {type === 'time' && (
          <KeyboardTimePicker
            fullWidth
            format={format || FORMAT_DEFAULT[type]}
            helperText={errors && helperText}
            id={id}
            value={currentDate}
            label={label}
            inputVariant="outlined"
            ampm={false}
            margin="normal"
            className={classes.container}
            onError={handleErrors}
            onChange={setCurrentDate}
            disabled={disabled}
            keyboardIcon={<AccessTimeIcon />}
            InputLabelProps={{ shrink }}
            error={error}
          />
        )}

        {type === 'datetime' && (
          <KeyboardDateTimePicker
            fullWidth
            InputLabelProps={{ shrink }}
            format={format || FORMAT_DEFAULT[type]}
            helperText={errors && helperText}
            id={id}
            value={currentDate === defaultValue ? null : currentDate}
            minDate={minDate}
            label={label}
            inputVariant="outlined"
            margin="normal"
            ampm={false}
            className={classes.container}
            onError={handleErrors}
            onChange={setCurrentDate}
            disabled={disabled}
            disablePast={disablePast}
            error={error}
          />
        )}
        {type === 'datetimeFilter' && (
          <KeyboardDateTimePicker
            fullWidth
            InputLabelProps={{ shrink }}
            format={FORMAT_DEFAULT.datetime}
            helperText={errors && helperText}
            id={id}
            value={currentDate === defaultValue ? null : currentDate}
            minDate={minDate}
            label={label}
            inputVariant="outlined"
            margin="normal"
            ampm={false}
            className={classes.container}
            onError={handleErrors}
            onChange={setCurrentDate}
            disabled={disabled}
            onClick={() => setDateOpen(!dateOpen)}
            open={dateOpen}
            onClose={() => setDateOpen(false)}
            disablePast={disablePast}
          />
        )}
      </MuiPickersUtilsProvider>
    </Grid>
  );
};

export default DatePicker;
